/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input, Modal, message, Tag, Select, DatePicker,
         Popconfirm, Tooltip, Row, Col, Checkbox, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
         CheckOutlined, CarOutlined, GlobalOutlined, FlagOutlined,
         WarningOutlined, ClockCircleOutlined, FileTextOutlined,
         DashboardOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
         CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FleetService from '../../services/FleetService';

const { TextArea } = Input;
const { Option }   = Select;
const { RangePicker } = DatePicker;

const TYPES               = ['Normal Duty','National Event','Cross Border'];
const REGIONS             = ['Hhohho','Manzini','Shiselweni','Lubombo','National'];
const NATIONAL_EVENTS     = ['Umhlanga','Emaganu Ceremony','Incwala','Lusekwane','Kuhlakula'];
const CROSS_BORDER_COUNTRIES = ['South Africa','Mozambique','Botswana','Namibia','Lesotho','Zimbabwe','Zambia','Malawi'];

const C = {
    blue:'#1a5b9a', orange:'#d46b08', green:'#389e0d',
    red:'#cf1322', purple:'#722ed1', amber:'#faad14', grey:'#595959',
};
const PIE_C = ['#1890ff','#faad14','#f5222d','#52c41a','#722ed1','#13c2c2'];

const fmt   = (v) => v || '—';
const fmtD  = (v) => (!v || v === '0000-00-00') ? '—' : moment(v).format('DD MMM YYYY');
const toN   = (v) => Number(v || 0);
const daysLeft = (expected) => {
    if (!expected || expected === '0000-00-00') return null;
    return moment(expected).diff(moment(), 'days');
};

// ─── Period Filter ────────────────────────────────────────────────────────────
const PRESETS = [
    { key:'this_month', label:'This Month',    from:() => moment().startOf('month'),           to:() => moment()                                         },
    { key:'last_3m',    label:'Last 3 Months', from:() => moment().subtract(3,'months'),       to:() => moment()                                         },
    { key:'this_year',  label:'This Year',     from:() => moment().startOf('year'),             to:() => moment()                                         },
    { key:'last_year',  label:'Last Year',     from:() => moment().subtract(1,'year').startOf('year'), to:() => moment().subtract(1,'year').endOf('year') },
    { key:'all',        label:'All Time',      from:() => null,                                 to:() => null                                             },
];
const DEFAULT_PERIOD = { preset:'this_year', from:moment().startOf('year').format('YYYY-MM-DD'), to:moment().format('YYYY-MM-DD') };

function PeriodFilter({ value, onChange }) {
    const cur = value || DEFAULT_PERIOD;
    const handlePreset = (preset) => {
        const p = PRESETS.find(x => x.key === preset);
        onChange({ preset, from: p.from() ? p.from().format('YYYY-MM-DD') : '', to: p.to() ? p.to().format('YYYY-MM-DD') : '' });
    };
    return (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            <span style={{ fontSize:11, color:'#888' }}>Period:</span>
            {PRESETS.map(p => (
                <button key={p.key} onClick={() => handlePreset(p.key)} style={{
                    padding:'3px 10px', border:'1px solid', borderRadius:12, cursor:'pointer',
                    fontSize:11, fontWeight: cur.preset===p.key ? 600 : 400,
                    background: cur.preset===p.key ? '#1a3a5c' : '#fff',
                    borderColor: cur.preset===p.key ? '#1a3a5c' : '#d9d9d9',
                    color: cur.preset===p.key ? '#fff' : '#595959',
                }}>{p.label}</button>
            ))}
            <RangePicker size="small" style={{ width:210 }}
                value={cur.preset==='custom' && cur.from && cur.to ? [moment(cur.from), moment(cur.to)] : null}
                onChange={([s,e]=[]) => onChange({ preset:'custom', from:s?s.format('YYYY-MM-DD'):'', to:e?e.format('YYYY-MM-DD'):'' })}
                format="DD MMM YYYY" placeholder={['Custom from','Custom to']} />
            {cur.from && cur.to && (
                <span style={{ fontSize:10, color:C.blue, background:'#e6f4ff', padding:'2px 8px', borderRadius:8, whiteSpace:'nowrap' }}>
                    {moment(cur.from).format('DD MMM YY')} → {moment(cur.to).format('DD MMM YY')}
                </span>
            )}
        </div>
    );
}

function filterByPeriod(data, period, dateField) {
    if (!period || period.preset === 'all' || (!period.from && !period.to)) return data;
    return data.filter(r => {
        const d = r[dateField];
        if (!d || d === '0000-00-00') return false;
        const m = moment(d);
        if (period.from && m.isBefore(moment(period.from), 'day')) return false;
        if (period.to   && m.isAfter(moment(period.to),   'day')) return false;
        return true;
    });
}

// ─── Anomaly Detector ─────────────────────────────────────────────────────────
function detectAnomalies(deps) {
    const issues = [];

    // Overdue > 7 days
    deps.filter(d => d.status !== 'Returned').forEach(d => {
        const dl = daysLeft(d.expected_return);
        if (dl !== null && dl < -7) {
            issues.push({ type:'error', msg:`"${d.event_name}" — ${Math.abs(dl)} days past return date, vehicles still out` });
        }
    });

    // International deployments with no vehicles returned > 30 days
    deps.filter(d => d.is_international == 1 && d.status !== 'Returned').forEach(d => {
        const dl = daysLeft(d.expected_return);
        if (dl !== null && dl < -3) {
            issues.push({ type:'error', msg:`International deployment "${d.event_name}" — ${Math.abs(dl)}d overdue, out of country vehicles not returned` });
        }
    });

    // Vehicles deployed with open accident
    deps.filter(d => d.status !== 'Returned').forEach(d => {
        (d.vehicles || []).filter(v => !v.date_returned && toN(v.has_open_incident) > 0).forEach(v => {
            issues.push({ type:'warn', msg:`${v.registration_no} is deployed on "${d.event_name}" but has an open accident incident` });
        });
    });

    // Very long active deployment > 30 days
    deps.filter(d => d.status === 'Active').forEach(d => {
        if (!d.date_out || d.date_out === '0000-00-00') return;
        const days = moment().diff(moment(d.date_out), 'days');
        if (days > 30) {
            issues.push({ type:'warn', msg:`"${d.event_name}" has been active for ${days} days — check if vehicles have returned` });
        }
    });

    return issues;
}

// ─── Mini Dashboard ───────────────────────────────────────────────────────────
function DeploymentDashboard({ deps, period, onPeriodChange }) {
    const periodDeps = filterByPeriod(deps, period, 'date_out');
    const anomalies  = useMemo(() => detectAnomalies(deps), [deps]);

    const byType = useMemo(() => {
        const m = {};
        periodDeps.forEach(d => { m[d.deployment_type||'Other'] = (m[d.deployment_type||'Other']||0)+1; });
        return Object.entries(m).map(([name,value]) => ({name,value})).sort((a,b)=>b.value-a.value);
    }, [periodDeps]);

    const byRegion = useMemo(() => {
        const m = {};
        periodDeps.forEach(d => { m[d.region||'Unknown'] = (m[d.region||'Unknown']||0)+1; });
        return Object.entries(m).map(([name,count]) => ({name,count})).sort((a,b)=>b.count-a.count).slice(0,6);
    }, [periodDeps]);

    const byMonth = useMemo(() => {
        const m = {};
        periodDeps.forEach(d => {
            if (!d.date_out || d.date_out==='0000-00-00') return;
            const mo = moment(d.date_out).format('MMM YY');
            m[mo] = (m[mo]||0)+1;
        });
        return Object.entries(m).slice(-8).map(([month,count]) => ({month,count}));
    }, [periodDeps]);

    const byStatus = useMemo(() => [
        { name:'Active',   value: periodDeps.filter(d=>d.status==='Active').length,   fill:'#1890ff' },
        { name:'Overdue',  value: periodDeps.filter(d=>d.status==='Overdue').length,  fill:C.red     },
        { name:'Returned', value: periodDeps.filter(d=>d.status==='Returned').length, fill:C.green   },
    ].filter(d=>d.value>0), [periodDeps]);

    const totalVehicleTrips = periodDeps.reduce((s,d) => s + (d.vehicles?.length||0), 0);
    const intlCount         = periodDeps.filter(d => d.is_international==1).length;
    const avgDuration       = useMemo(() => {
        const withDates = periodDeps.filter(d => d.date_out && d.date_out!=='0000-00-00'
            && d.expected_return && d.expected_return!=='0000-00-00');
        if (!withDates.length) return null;
        const total = withDates.reduce((s,d) =>
            s + moment(d.expected_return).diff(moment(d.date_out),'days'), 0);
        return (total / withDates.length).toFixed(0);
    }, [periodDeps]);

    return (
        <div style={{ background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:20 }}>

            {/* Period filter */}
            <div style={{ marginBottom:12 }}>
                <PeriodFilter value={period} onChange={onPeriodChange} />
            </div>

            {/* Anomaly banner */}
            {anomalies.length > 0 && (
                <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:6,
                    padding:'8px 14px', marginBottom:14 }}>
                    <div style={{ fontWeight:700, color:C.red, fontSize:12, marginBottom:6 }}>
                        <WarningOutlined style={{ marginRight:6 }} />
                        {anomalies.length} irregularit{anomalies.length>1?'ies':'y'} detected
                    </div>
                    {anomalies.map((a,i) => (
                        <div key={i} style={{ fontSize:11, display:'flex', gap:6, marginBottom:3 }}>
                            <Tag color={a.type==='error'?'red':'orange'} style={{ fontSize:9, margin:0 }}>
                                {a.type==='error'?'Error':'Warning'}
                            </Tag>
                            {a.msg}
                        </div>
                    ))}
                </div>
            )}

            {/* Insight pills */}
            <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
                {[
                    { label:'Deployments in Period', value: periodDeps.length,                     color: C.blue   },
                    { label:'Vehicle Trips',          value: totalVehicleTrips,                     color: C.orange },
                    { label:'International',          value: intlCount,                             color: C.purple },
                    { label:'Avg Duration',           value: avgDuration ? `${avgDuration} days`:'—', color: C.grey },
                    { label:'Currently Out',          value: deps.filter(d=>d.status!=='Returned').reduce((s,d)=>s+(d.vehicles||[]).filter(v=>!v.date_returned).length,0), color: C.amber },
                ].map(({label,value,color}) => (
                    <div key={label} style={{ background:'#fff', border:'1px solid #e8e8e8',
                        borderRadius:6, padding:'6px 12px', borderLeft:`3px solid ${color}` }}>
                        <div style={{ fontSize:10, color:'#888' }}>{label}</div>
                        <div style={{ fontSize:16, fontWeight:700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* Charts */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1.3fr 1.3fr 1fr', gap:12 }}>

                {/* Status pie */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>By Status</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                            <Pie data={byStatus} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={55} labelLine={false}
                                label={({name,percent}) => `${(percent*100).toFixed(0)}%`}>
                                {byStatus.map((d,i) => <Cell key={i} fill={d.fill} />)}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={8} wrapperStyle={{ fontSize:9 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Type bar */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>By Deployment Type</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={byType} margin={{ top:0, right:5, left:-20, bottom:0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={{ fontSize:9 }} />
                            <YAxis tick={{ fontSize:9 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="value" radius={[3,3,0,0]}>
                                {byType.map((_,i) => <Cell key={i} fill={PIE_C[i%PIE_C.length]} />)}
                                <LabelList dataKey="value" position="top" style={{ fontSize:9 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Monthly trend */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>Monthly Trend</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={byMonth} margin={{ top:0, right:5, left:-20, bottom:0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" tick={{ fontSize:9 }} />
                            <YAxis tick={{ fontSize:9 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.orange} radius={[3,3,0,0]}>
                                <LabelList dataKey="count" position="top" style={{ fontSize:9 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* By region */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>By Region</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={byRegion} layout="vertical"
                            margin={{ top:0, right:30, left:55, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:9 }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:9 }} width={50} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.purple} radius={[0,3,3,0]}>
                                <LabelList dataKey="count" position="right" style={{ fontSize:9 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status, days_overdue }) {
    const cfg = {
        Active:   { bg:'#f0f5ff', border:'#adc6ff', color:'#2f54eb', icon:<ClockCircleOutlined /> },
        Overdue:  { bg:'#fff1f0', border:'#ffa39e', color:C.red,     icon:<WarningOutlined /> },
        Returned: { bg:'#f6ffed', border:'#b7eb8f', color:C.green,   icon:<CheckOutlined /> },
    }[status] || { bg:'#fafafa', border:'#d9d9d9', color:C.grey, icon:null };
    return (
        <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10,
            background:cfg.bg, border:`1px solid ${cfg.border}`, color:cfg.color,
            display:'inline-flex', alignItems:'center', gap:4 }}>
            {cfg.icon}{status}
            {status==='Overdue' && days_overdue > 0 ? ` (${days_overdue}d)` : ''}
        </span>
    );
}

// ─── Type Badge ───────────────────────────────────────────────────────────────
function TypeBadge({ type, isIntl }) {
    const cfg = {
        'Normal Duty':    { color:C.grey,   icon:<CarOutlined /> },
        'National Event': { color:C.orange, icon:<FlagOutlined /> },
        'Cross Border':   { color:C.purple, icon:<GlobalOutlined /> },
    }[type] || { color:C.grey, icon:<CarOutlined /> };
    return (
        <span style={{ fontSize:11, fontWeight:600, color:cfg.color,
            display:'inline-flex', alignItems:'center', gap:4 }}>
            {cfg.icon} {type}
            {isIntl==1 && <Tag color="purple" style={{ fontSize:9, margin:0 }}>🌍 Intl</Tag>}
        </span>
    );
}

// ─── Deployment Form ──────────────────────────────────────────────────────────
function DeploymentForm({ open, dep, vehicles, drivers, onSaved, onClose }) {
    const EMPTY = {
        id:null, deployment_type:'Normal Duty', event_name:'', description:'',
        region:'', destination:'', is_international:0,
        date_out:null, expected_return:null, notes:'', vehicles:[],
    };
    const [form,setForm]     = useState(EMPTY);
    const [saving,setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            if (dep) {
                setForm({ ...EMPTY, ...dep,
                    vehicles: (dep.vehicles||[]).map(v => ({
                        vehicle_id: v.vehicle_id, driver_id: v.driver_id||null, notes: v.notes||'',
                    })),
                });
            } else { setForm({ ...EMPTY, vehicles:[] }); }
        }
    }, [open, dep?.id]);

    const set = (k,v) => setForm(p => ({...p,[k]:v}));

    const toggleVehicle = (id) => {
        const exists = form.vehicles.find(v => v.vehicle_id===id);
        if (exists) set('vehicles', form.vehicles.filter(v => v.vehicle_id!==id));
        else        set('vehicles', [...form.vehicles, {vehicle_id:id,driver_id:null,notes:''}]);
    };
    const setVehicleField = (vehId,field,val) =>
        set('vehicles', form.vehicles.map(v => v.vehicle_id===vehId ? {...v,[field]:val} : v));

    const handleSave = async () => {
        const eventName = form.event_name || form.destination;
        if (!eventName)          { message.error(form.deployment_type==='National Event'?'Select a national event':form.deployment_type==='Cross Border'?'Select destination country':'Enter assignment name'); return; }
        if (!form.date_out)      { message.error('Enter the date out'); return; }
        if (!form.expected_return){ message.error('Enter expected return date'); return; }
        if (!form.vehicles.length){ message.error('Select at least one vehicle'); return; }
        setSaving(true);
        try {
            const payload = new FormData();
            Object.entries(form).forEach(([k,v]) => {
                if (k==='vehicles') payload.append('vehicles', JSON.stringify(v));
                else if (k==='date_out'||k==='expected_return') payload.append(k, v?moment(v).format('YYYY-MM-DD'):'');
                else payload.append(k, v??'');
            });
            const res = await FleetService.saveDeployment(payload);
            if (Number(res?.data?.code)===200) { message.success('Deployment saved'); onSaved(); }
            else message.error(res?.data?.message||'Failed');
        } catch(e) { message.error(e.message); }
        finally { setSaving(false); }
    };

    const lbl   = { fontSize:12, color:'#666', marginBottom:4 };
    const fbox  = { marginBottom:14 };
    const selIds = new Set(form.vehicles.map(v => v.vehicle_id));

    return (
        <Modal open={open} onCancel={onClose} width={800} footer={null} destroyOnClose
            title={<div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <FlagOutlined style={{ color:C.orange }} />
                <span>{form.id ? 'Edit Deployment' : 'New Vehicle Deployment'}</span>
            </div>}>

            {/* Deployment Details */}
            <div style={{ background:'#fff7e6', border:'1px solid #ffd591', borderRadius:6, padding:'14px 16px', marginBottom:14 }}>
                <div style={{ fontWeight:600, color:C.orange, marginBottom:12 }}>📋 Deployment Details</div>
                <Row gutter={16}>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Region <span style={{ fontSize:10, color:'#1677ff', marginLeft:4 }}>filters vehicles ↓</span></div>
                            <Select style={{ width:'100%' }} value={form.region}
                                onChange={v => { set('region', v||''); set('vehicles',[]); }}
                                allowClear placeholder="All regions">
                                {REGIONS.map(r => <Option key={r} value={r}>{r}</Option>)}
                            </Select>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Deployment Type</div>
                            <Select style={{ width:'100%' }} value={form.deployment_type}
                                onChange={v => { set('deployment_type',v); set('event_name',''); set('destination',''); set('is_international',v==='Cross Border'?1:0); }}>
                                {TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                            </Select>
                        </div>
                    </Col>
                    <Col span={8}>
                        {form.deployment_type==='National Event' && (
                            <div style={fbox}><div style={lbl}>National Event <span style={{ color:'red' }}>*</span></div>
                                <Select style={{ width:'100%' }} value={form.event_name||undefined} onChange={v=>set('event_name',v)} placeholder="Select event">
                                    {NATIONAL_EVENTS.map(e=><Option key={e} value={e}>{e}</Option>)}
                                </Select>
                            </div>
                        )}
                        {form.deployment_type==='Cross Border' && (
                            <div style={fbox}><div style={lbl}>Destination Country <span style={{ color:'red' }}>*</span></div>
                                <Select style={{ width:'100%' }} value={form.destination||undefined}
                                    onChange={v => { set('destination',v); set('event_name',`Cross-Border Mission — ${v}`); set('is_international',1); }}
                                    placeholder="Select country">
                                    {CROSS_BORDER_COUNTRIES.map(c=><Option key={c} value={c}>{c}</Option>)}
                                </Select>
                            </div>
                        )}
                        {form.deployment_type==='Normal Duty' && (
                            <div style={fbox}><div style={lbl}>Assignment Name <span style={{ color:'red' }}>*</span></div>
                                <Input value={form.event_name} onChange={e=>set('event_name',e.target.value)} placeholder="e.g. Outreach Programme" />
                            </div>
                        )}
                    </Col>
                    <Col span={8}>
                        <div style={fbox}><div style={lbl}>Date Out <span style={{ color:'red' }}>*</span></div>
                            <DatePicker style={{ width:'100%' }} value={form.date_out?moment(form.date_out):null}
                                onChange={d=>set('date_out',d?d.format('YYYY-MM-DD'):null)} format="DD MMM YYYY" />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}><div style={lbl}>Expected Return <span style={{ color:'red' }}>*</span></div>
                            <DatePicker style={{ width:'100%' }} value={form.expected_return?moment(form.expected_return):null}
                                onChange={d=>set('expected_return',d?d.format('YYYY-MM-DD'):null)} format="DD MMM YYYY" />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}><div style={lbl}>International Trip?</div>
                            <Switch checked={form.is_international==1} onChange={v=>set('is_international',v?1:0)}
                                checkedChildren="🌍 Yes" unCheckedChildren="No"
                                disabled={form.deployment_type==='Cross Border'} />
                            {form.deployment_type==='Cross Border' && <span style={{ fontSize:10, color:C.purple, marginLeft:8 }}>Auto-set</span>}
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={fbox}><div style={lbl}>Notes</div>
                            <TextArea rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Additional details..." />
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Vehicle Selection */}
            <div style={{ background:'#f0f5ff', border:'1px solid #adc6ff', borderRadius:6, padding:'14px 16px', marginBottom:20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <div style={{ fontWeight:600, color:'#2f54eb' }}>
                        🚗 Select Vehicles ({form.vehicles.length} selected)
                        {form.region && <span style={{ fontSize:11, color:'#1677ff', marginLeft:8, fontWeight:400 }}>
                            — {vehicles.filter(v=>v.region===form.region).length} in {form.region}
                        </span>}
                    </div>
                </div>
                <div style={{ maxHeight:300, overflowY:'auto' }}>
                    {(form.region ? vehicles.filter(v=>v.region===form.region) : vehicles).map(v => {
                        const checked  = selIds.has(v.id);
                        const sel      = form.vehicles.find(x => x.vehicle_id===v.id);
                        const hasAcc   = toN(v.has_open_incident) > 0;
                        const svcDate  = v.last_service_date && v.last_service_date!=='0000-00-00'
                            ? moment(v.last_service_date).format('DD MMM YYYY') : null;
                        const svcFlag  = v.service_due_flag;
                        return (
                            <div key={v.id} style={{
                                border:`1px solid ${checked?'#adc6ff':hasAcc?'#ffa39e':'#e8e8e8'}`,
                                borderRadius:6, padding:'10px 12px', marginBottom:6,
                                background: checked?'#f0f5ff':hasAcc?'#fff9f9':'#fafafa',
                            }}>
                                <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                                    <Checkbox checked={checked} onChange={()=>toggleVehicle(v.id)} style={{ marginTop:2 }} />
                                    <div style={{ flex:1 }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                            <span style={{ fontWeight:700, color:C.blue, fontSize:13 }}>{v.registration_no}</span>
                                            <span style={{ color:'#888', fontSize:11 }}>{v.vehicle_type} · {v.fuel_type||'—'}</span>
                                            {hasAcc && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:8, background:'#fff1f0', border:'1px solid #ffa39e', color:C.red }}>⚠ ACCIDENT</span>}
                                            {svcFlag==='overdue'   && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:8, background:'#fff1f0', border:'1px solid #ffa39e', color:C.red }}>🔧 OVERDUE</span>}
                                            {svcFlag==='due_soon'  && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:8, background:'#fffbe6', border:'1px solid #ffe58f', color:'#d48806' }}>🔧 DUE SOON</span>}
                                        </div>
                                        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'2px 12px', marginTop:5, fontSize:11 }}>
                                            <div><span style={{ color:'#aaa' }}>Programme: </span><span style={{ fontWeight:500 }}>{v.assigned_programme||'—'}</span></div>
                                            <div><span style={{ color:'#aaa' }}>Driver: </span><span style={{ fontWeight:500, color:v.current_driver_name?C.green:'#aaa' }}>{v.current_driver_name||'Unassigned'}</span></div>
                                            <div><span style={{ color:'#aaa' }}>Mileage: </span><span style={{ fontWeight:500 }}>{v.current_odometer?`${toN(v.current_odometer).toLocaleString()} km`:'—'}</span></div>
                                            <div><span style={{ color:'#aaa' }}>Last Service: </span><span style={{ fontWeight:500, color:svcFlag==='overdue'?C.red:svcFlag==='due_soon'?'#d48806':'#333' }}>{svcDate||'Never'}</span></div>
                                        </div>
                                    </div>
                                </div>
                                {checked && (
                                    <Row gutter={12} style={{ marginTop:10, paddingTop:8, borderTop:'1px dashed #adc6ff' }}>
                                        <Col span={12}>
                                            <Select style={{ width:'100%' }} size="small" value={sel?.driver_id||undefined}
                                                onChange={val=>setVehicleField(v.id,'driver_id',val)}
                                                placeholder={v.current_driver_name?`Current: ${v.current_driver_name}`:'Assign deployment driver (optional)'}
                                                allowClear showSearch optionFilterProp="children">
                                                {drivers.map(d=><Option key={d.id} value={d.id}>{d.name}{d.licence_days_remaining<30?' ⚠ licence expires soon':''}</Option>)}
                                            </Select>
                                        </Col>
                                        <Col span={12}>
                                            <Input size="small" value={sel?.notes||''}
                                                onChange={e=>setVehicleField(v.id,'notes',e.target.value)}
                                                placeholder="Vehicle-specific notes" />
                                        </Col>
                                    </Row>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button type="primary" loading={saving} onClick={handleSave}
                    style={{ background:C.orange, borderColor:C.orange }}>
                    {form.id ? 'Update Deployment' : 'Create Deployment'}
                </Button>
            </div>
        </Modal>
    );
}

// ─── Deployment Detail Modal ──────────────────────────────────────────────────
function DeploymentDetail({ dep, onClose, onReturn, onReturnVehicle }) {
    if (!dep) return null;
    const overdue = daysLeft(dep.expected_return);
    return (
        <Modal open={!!dep} onCancel={onClose} width={680} destroyOnClose
            footer={
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <div>
                        {dep.status !== 'Returned' && (
                            <Popconfirm title="Mark entire deployment as returned?"
                                onConfirm={() => { onReturn(dep.id); onClose(); }} okText="Confirm">
                                <Button type="primary" icon={<CheckOutlined />}
                                    style={{ background:C.green, borderColor:C.green }}>
                                    Mark All Returned
                                </Button>
                            </Popconfirm>
                        )}
                    </div>
                    <Button onClick={onClose}>Close</Button>
                </div>
            }
            title={<div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <FileTextOutlined />
                <span>{dep.event_name}</span>
                <StatusBadge status={dep.status} days_overdue={dep.days_overdue} />
                {dep.is_international==1 && <Tag color="purple">🌍 International</Tag>}
            </div>}>

            <Row gutter={16} style={{ marginBottom:16 }}>
                <Col span={8}><div style={{ fontSize:11, color:'#888' }}>Type</div><TypeBadge type={dep.deployment_type} isIntl={dep.is_international} /></Col>
                <Col span={8}><div style={{ fontSize:11, color:'#888' }}>Date Out</div><div style={{ fontWeight:600 }}>{fmtD(dep.date_out)}</div></Col>
                <Col span={8}><div style={{ fontSize:11, color:'#888' }}>Expected Return</div>
                    <div style={{ fontWeight:600, color: overdue!==null&&overdue<0?C.red:'#333' }}>
                        {fmtD(dep.expected_return)}{overdue!==null&&overdue<0?` (${Math.abs(overdue)}d overdue)`:''}
                    </div>
                </Col>
                {dep.destination && <Col span={12}><div style={{ fontSize:11, color:'#888' }}>Destination</div><div>{dep.destination}</div></Col>}
                {dep.region      && <Col span={12}><div style={{ fontSize:11, color:'#888' }}>Region</div><div>{dep.region}</div></Col>}
                {dep.notes       && <Col span={24}><div style={{ fontSize:11, color:'#888' }}>Notes</div>
                    <div style={{ fontSize:12, background:'#fafafa', padding:'6px 10px', borderRadius:4 }}>{dep.notes}</div></Col>}
            </Row>

            <div style={{ fontWeight:600, fontSize:12, marginBottom:8, color:'#1a3a5c' }}>🚗 Vehicles ({dep.vehicles?.length||0})</div>
            <div style={{ border:'1px solid #e8e8e8', borderRadius:6, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead>
                        <tr style={{ background:'#1a3a5c', color:'#fff' }}>
                            {['Reg. Plate','Type','Facility','Driver','Status','Action'].map(h => (
                                <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:11, fontWeight:600 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {(dep.vehicles||[]).map((v,i) => (
                            <tr key={v.vehicle_id} style={{ background:i%2===0?'#fff':'#f9f9f9', borderBottom:'1px solid #f0f0f0' }}>
                                <td style={{ padding:'8px 10px', fontWeight:700, color:C.blue }}>{v.registration_no}</td>
                                <td style={{ padding:'8px 10px' }}>{v.vehicle_type}</td>
                                <td style={{ padding:'8px 10px', fontSize:11, color:'#888' }}>{v.facility}</td>
                                <td style={{ padding:'8px 10px' }}>{v.driver_name||'—'}</td>
                                <td style={{ padding:'8px 10px' }}>
                                    {v.date_returned
                                        ? <Tag color="success">Returned {fmtD(v.date_returned)}</Tag>
                                        : v.vehicle_status==='Overdue'
                                            ? <Tag color="error">Overdue</Tag>
                                            : <Tag color="processing">Out</Tag>}
                                </td>
                                <td style={{ padding:'8px 10px' }}>
                                    {!v.date_returned && dep.status!=='Returned' && (
                                        <Popconfirm title={`Mark ${v.registration_no} as returned?`}
                                            onConfirm={() => onReturnVehicle(dep.id, v.vehicle_id)} okText="Confirm">
                                            <Button size="small" icon={<CheckOutlined />} type="link">Return</Button>
                                        </Popconfirm>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Modal>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FleetDeployments() {
    const [deps,     setDeps]     = useState([]);
    const [stats,    setStats]    = useState({});
    const [vehicles, setVehicles] = useState([]);
    const [drivers,  setDrivers]  = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [editing,  setEditing]  = useState(null);
    const [detail,   setDetail]   = useState(null);
    const [showDash, setShowDash] = useState(true);
    const [period,   setPeriod]   = useState(DEFAULT_PERIOD);

    // Filters
    const [search,       setSearch]       = useState('');
    const [filterType,   setFilterType]   = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterRegion, setFilterRegion] = useState('');
    const [filterIntl,   setFilterIntl]   = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [dRes, vRes, drRes] = await Promise.all([
                FleetService.getDeployments(),
                FleetService.getVehiclesList(),
                FleetService.getDriversList(),
            ]);
            if (Number(dRes?.data?.code)===200) {
                setDeps(dRes.data.result?.deployments || []);
                setStats(dRes.data.result?.stats || {});
            }
            if (Number(vRes?.data?.code)===200)  setVehicles(vRes.data.result || []);
            if (Number(drRes?.data?.code)===200)  setDrivers(drRes.data.result || []);
        } catch { message.error('Failed to load deployments'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleReturn = async (id) => {
        await FleetService.returnDeployment(id);
        message.success('Deployment marked as returned'); load();
    };
    const handleReturnVehicle = async (depId, vehId) => {
        await FleetService.returnVehicle({ deployment_id:depId, vehicle_id:vehId });
        message.success('Vehicle returned'); load();
    };
    const handleDelete = async (id) => {
        await FleetService.deleteDeployment(id);
        message.success('Deployment deleted'); load();
    };

    // Export
    const exportXlsx = () => {
        const rows = filtered.map(d => ({
            'Event / Assignment': d.event_name,
            'Type':               d.deployment_type,
            'Region':             d.region || '—',
            'Destination':        d.destination || '—',
            'International':      d.is_international==1 ? 'Yes' : 'No',
            'Date Out':           d.date_out,
            'Expected Return':    d.expected_return,
            'Status':             d.status,
            'Vehicles':           (d.vehicles||[]).map(v=>v.registration_no).join(', '),
            'Vehicle Count':      (d.vehicles||[]).length,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Deployments');
        saveAs(new Blob([XLSX.write(wb,{bookType:'xlsx',type:'array'})]),
            `Deployments_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    // Unique regions for filter
    const regions = useMemo(() => [...new Set(deps.map(d=>d.region).filter(Boolean))].sort(), [deps]);

    // Filter
    const filtered = useMemo(() => deps.filter(d => {
        const q  = search.toLowerCase();
        const ms = !q || (d.event_name||'').toLowerCase().includes(q)
                      || (d.destination||'').toLowerCase().includes(q)
                      || (d.region||'').toLowerCase().includes(q)
                      || (d.vehicles||[]).some(v=>(v.registration_no||'').toLowerCase().includes(q));
        const mt = !filterType   || d.deployment_type === filterType;
        const ms2= !filterStatus || d.status          === filterStatus;
        const mr = !filterRegion || d.region          === filterRegion;
        const mi = !filterIntl   || d.is_international == 1;
        return ms && mt && ms2 && mr && mi;
    }), [deps, search, filterType, filterStatus, filterRegion, filterIntl]);

    // Overdue vehicles
    const overdueVehicles = useMemo(() => {
        const list = [];
        deps.filter(d=>d.status!=='Returned').forEach(d => {
            const dl = daysLeft(d.expected_return);
            if (dl!==null && dl<0) {
                (d.vehicles||[]).filter(v=>!v.date_returned).forEach(v => {
                    list.push({...v, event_name:d.event_name, days_overdue:Math.abs(dl), is_international:d.is_international});
                });
            }
        });
        return list;
    }, [deps]);

    const outVehicles = useMemo(() => {
        const s = new Set();
        deps.filter(d=>d.status!=='Returned').forEach(d =>
            (d.vehicles||[]).filter(v=>!v.date_returned).forEach(v => s.add(v.vehicle_id)));
        return s;
    }, [deps]);

    const anomalyCount = useMemo(() => detectAnomalies(deps).length, [deps]);

    return (
        <div className="page-content">
            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700 }}>Vehicle Deployments</h4>
                    <div style={{ fontSize:13, color:'#888', marginTop:2 }}>
                        Track Normal Duty · National Events · Cross-Border assignments
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    <Button icon={<DashboardOutlined />}
                        type={showDash?'primary':'default'} ghost={showDash}
                        onClick={() => setShowDash(p=>!p)}>
                        {showDash ? 'Hide Dashboard' : 'Show Dashboard'}
                        {anomalyCount>0 && !showDash && (
                            <span style={{ marginLeft:6, background:C.red, color:'#fff',
                                borderRadius:8, padding:'0 5px', fontSize:10 }}>{anomalyCount}</span>
                        )}
                    </Button>
                    <Button icon={<DownloadOutlined />} onClick={exportXlsx} disabled={!filtered.length}>
                        Export
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />}
                        onClick={() => { setEditing(null); setFormOpen(true); }}
                        style={{ background:C.orange, borderColor:C.orange }}>
                        New Deployment
                    </Button>
                </div>
            </div>

            {/* KPI Cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:20 }}>
                {[
                    { label:'Total',          value:stats.total||0,         color:C.grey   },
                    { label:'Active',         value:stats.active||0,        color:'#1890ff' },
                    { label:'Overdue',        value:stats.overdue||0,       color:C.red    },
                    { label:'Returned',       value:stats.returned||0,      color:C.green  },
                    { label:'International',  value:stats.international||0, color:C.purple },
                ].map(({label,value,color}) => (
                    <div key={label} style={{ background:'#fff', border:'1px solid #e8e8e8',
                        borderRadius:8, padding:'14px 16px', borderTop:`3px solid ${color}` }}>
                        <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
                        <div style={{ fontSize:26, fontWeight:700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* Overdue alert */}
            {overdueVehicles.length > 0 && (
                <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:8,
                    padding:'12px 16px', marginBottom:16 }}>
                    <div style={{ fontWeight:700, color:C.red, marginBottom:8 }}>
                        ⚠ {overdueVehicles.length} vehicle{overdueVehicles.length>1?'s':''} past expected return date
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                        {overdueVehicles.map((v,i) => (
                            <div key={i} style={{ background:'#fff', border:'1px solid #ffa39e',
                                borderRadius:6, padding:'4px 10px', fontSize:12 }}>
                                <span style={{ fontWeight:700, color:C.blue }}>{v.registration_no}</span>
                                <span style={{ color:'#aaa', margin:'0 6px' }}>·</span>
                                <span>{v.event_name}</span>
                                <span style={{ marginLeft:6, fontWeight:700, color:C.red }}>{v.days_overdue}d overdue</span>
                                {v.is_international==1 && <Tag color="purple" style={{ marginLeft:6, fontSize:9 }}>🌍</Tag>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Mini Dashboard */}
            {showDash && deps.length > 0 && (
                <DeploymentDashboard deps={deps} period={period} onPeriodChange={setPeriod} />
            )}

            {/* Filters */}
            <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap',
                background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px 14px' }}>
                <Input prefix={<SearchOutlined style={{ color:'#ccc' }} />}
                    placeholder="Search event, vehicle plate, destination, region…"
                    value={search} onChange={e => setSearch(e.target.value)}
                    allowClear style={{ width:280 }} />
                <Select placeholder="Type" value={filterType||undefined}
                    onChange={setFilterType} allowClear style={{ width:160 }}>
                    {TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                </Select>
                <Select placeholder="Status" value={filterStatus||undefined}
                    onChange={setFilterStatus} allowClear style={{ width:130 }}>
                    <Option value="Active">Active</Option>
                    <Option value="Overdue">Overdue</Option>
                    <Option value="Returned">Returned</Option>
                </Select>
                <Select placeholder="Region" value={filterRegion||undefined}
                    onChange={setFilterRegion} allowClear style={{ width:130 }}>
                    {regions.map(r => <Option key={r} value={r}>{r}</Option>)}
                </Select>
                <div style={{ display:'flex', alignItems:'center', gap:6,
                    background: filterIntl?'#f9f0ff':'transparent',
                    border: filterIntl?'1px solid #d3adf7':'1px solid #d9d9d9',
                    borderRadius:6, padding:'0 10px', cursor:'pointer' }}
                    onClick={() => setFilterIntl(p=>!p)}>
                    <GlobalOutlined style={{ color: filterIntl?C.purple:'#aaa', fontSize:13 }} />
                    <span style={{ fontSize:12, color: filterIntl?C.purple:'#595959', fontWeight: filterIntl?600:400 }}>
                        International only
                    </span>
                </div>
                <div style={{ marginLeft:'auto', fontSize:12, color:'#888', alignSelf:'center' }}>
                    {filtered.length} of {deps.length}
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign:'center', padding:60, color:'#aaa' }}>Loading deployments…</div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign:'center', padding:60, color:'#aaa', border:'2px dashed #e8e8e8', borderRadius:8 }}>
                    <FlagOutlined style={{ fontSize:40, marginBottom:12, display:'block' }} />
                    {deps.length===0 ? 'No deployments yet.' : 'No records match your filters.'}
                </div>
            ) : (
                <div style={{ border:'1px solid #e8e8e8', borderRadius:8, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                            <tr style={{ background:'#1a3a5c', color:'#fff' }}>
                                {['Event / Assignment','Type','Region','Date Out','Exp. Return','Days','Vehicles','Status','Actions'].map(h => (
                                    <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((d, idx) => {
                                const dl = daysLeft(d.expected_return);
                                const actualDays = d.date_out && d.date_out!=='0000-00-00'
                                    ? moment().diff(moment(d.date_out),'days') : null;
                                return (
                                    <tr key={d.id}
                                        style={{ background:idx%2===0?'#fff':'#f9f9f9',
                                            borderBottom:'1px solid #f0f0f0', cursor:'pointer' }}
                                        onClick={() => setDetail(d)}>
                                        <td style={{ padding:'10px 12px' }}>
                                            <div style={{ fontWeight:700 }}>{d.event_name}</div>
                                            {d.destination && <div style={{ fontSize:11, color:C.purple }}>📍 {d.destination}</div>}
                                            {d.is_international==1 && <Tag color="purple" style={{ fontSize:9, margin:0 }}>🌍 International</Tag>}
                                        </td>
                                        <td style={{ padding:'10px 12px' }}><TypeBadge type={d.deployment_type} /></td>
                                        <td style={{ padding:'10px 12px', fontSize:11, color:C.purple }}>
                                            {d.region || '—'}
                                        </td>
                                        <td style={{ padding:'10px 12px', whiteSpace:'nowrap' }}>{fmtD(d.date_out)}</td>
                                        <td style={{ padding:'10px 12px', whiteSpace:'nowrap',
                                            color: dl!==null&&dl<0?C.red:'#333',
                                            fontWeight: dl!==null&&dl<0?700:400 }}>
                                            {fmtD(d.expected_return)}
                                        </td>
                                        <td style={{ padding:'10px 12px', fontWeight:700,
                                            color: dl===null?'#aaa':dl<0?C.red:dl<=3?C.orange:C.green }}>
                                            {dl===null ? '—' : dl<0 ? `${Math.abs(dl)}d over` : `${dl}d left`}
                                        </td>
                                        <td style={{ padding:'10px 12px' }}>
                                            <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                                                {(d.vehicles||[]).slice(0,3).map(v => (
                                                    <Tag key={v.vehicle_id} style={{ fontSize:10, margin:0 }}>
                                                        {v.registration_no}{v.date_returned?' ✓':''}
                                                    </Tag>
                                                ))}
                                                {(d.vehicles||[]).length>3 && <Tag style={{ fontSize:10, margin:0 }}>+{d.vehicles.length-3}</Tag>}
                                            </div>
                                        </td>
                                        <td style={{ padding:'10px 12px' }}>
                                            <StatusBadge status={d.status} days_overdue={d.days_overdue} />
                                        </td>
                                        <td style={{ padding:'10px 12px' }} onClick={e=>e.stopPropagation()}>
                                            <div style={{ display:'flex', gap:5 }}>
                                                {d.status!=='Returned' && (
                                                    <Tooltip title="Mark returned">
                                                        <Popconfirm title="Mark entire deployment as returned?"
                                                            onConfirm={()=>handleReturn(d.id)} okText="Confirm">
                                                            <Button size="small" icon={<CheckOutlined />}
                                                                style={{ background:C.green, borderColor:C.green, color:'#fff' }} />
                                                        </Popconfirm>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Edit">
                                                    <Button size="small" icon={<EditOutlined />}
                                                        onClick={()=>{setEditing(d);setFormOpen(true);}} />
                                                </Tooltip>
                                                <Popconfirm title="Delete this deployment?"
                                                    onConfirm={()=>handleDelete(d.id)}
                                                    okText="Delete" okButtonProps={{ danger:true }}>
                                                    <Button size="small" danger icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{ background:'#f0f5ff', borderTop:'2px solid #adc6ff' }}>
                                <td colSpan={6} style={{ padding:'8px 12px', fontWeight:700, fontSize:12, color:'#2f54eb' }}>
                                    TOTAL ({filtered.length} deployments)
                                </td>
                                <td style={{ padding:'8px 12px', fontWeight:700, color:C.orange }}>
                                    {filtered.reduce((s,d)=>s+(d.vehicles?.length||0),0)} vehicle trips
                                </td>
                                <td colSpan={2} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            <DeploymentForm open={formOpen} dep={editing} vehicles={vehicles} drivers={drivers}
                onSaved={()=>{setFormOpen(false);load();}} onClose={()=>setFormOpen(false)} />
            <DeploymentDetail dep={detail} onClose={()=>setDetail(null)}
                onReturn={handleReturn} onReturnVehicle={handleReturnVehicle} />
        </div>
    );
}